// pages/SectorsPage.tsx
import { useState, useMemo } from "react";
import { Plus, Search, MoreVertical, Grid3X3, Filter, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

import MenuModal from "../../../components/ui/MenuModal";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import PageLayout from "../../../layouts/PageListLayout";
import FlexContainer from "../../../components/ui/FlexContainer";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import { useAuth, useSectors } from "@cabukaka/hooks";
import SectorsList from "../../../components/features/sectors/SectorsList";
import { Sector } from "@cabukaka/types";


export default function SectorsPage() {
  const navigate = useNavigate();
  const { user } = useAuth()
  const role = user?.role;

  const { sectors, deleteSector, deleteManySectors } = useSectors();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSector, setSelectedSector] = useState<Sector | null>(null);
  const [sectorToDelete, setSectorToDelete] = useState<Sector | null>(null);
  const [itemsToDelete, setItemsToDelete] = useState<string[] | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Filtrage des secteurs
  const filteredSectors = useMemo(() => {
    let result = sectors;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      result = result.filter(s =>
        s.name?.toLowerCase().includes(searchLower) ||
        s.slug?.toLowerCase().includes(searchLower)
      );
    }

    return result;
  }, [sectors, searchTerm]);

  const stats = useMemo(() => ({
    total: sectors.length
  }), [sectors]);

  const handleDelete = async () => {
    if (!sectorToDelete?.id) return;
    setIsDeleting(true);
    try {
      await deleteSector(sectorToDelete.id);
      setSectorToDelete(null);
      alertSuccess("Secteur supprimé avec succès");
    } catch (err) {
      alertError("Erreur lors de la suppression");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteMany = async () => {
    if (!itemsToDelete || itemsToDelete.length === 0) return;
    try {
      await deleteManySectors(itemsToDelete);
      setItemsToDelete(null);
      alertSuccess(`${itemsToDelete.length} secteur(s) supprimé(s)`);
    } catch (err) {
      alertError("Erreur lors de la suppression multiple");
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
  };

  const hasActiveFilters = searchTerm;



  return (
    <PageLayout
      title="Gestion des secteurs"
      description={`${filteredSectors.length} secteur${filteredSectors.length > 1 ? 's' : ''} affiché${filteredSectors.length > 1 ? 's' : ''}`}
      actions={
        <FlexContainer className="animate-fade-in-up w-full">
          <button
            onClick={() => { navigate(`/${role}/sectors/new`) }}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-primary to-primary/80 text-white rounded-xl"
          >
            <Plus size={18} /> Ajouter
          </button>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all ${showFilters ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <Filter size={18} /> Filtrer
            {hasActiveFilters && <span className="ml-1 w-2 h-2 bg-red-500 rounded-full" />}
          </button>
        </FlexContainer>
      }
     
    >
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-1 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5">
          <p className="text-xs font-semibold text-blue-700/70">Total secteurs</p>
          <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
        </div>
      </div>

      {/* Recherche */}
      <div className="relative mb-4">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Rechercher un secteur..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
        />
      </div>

      {/* Filtres */}
      {showFilters && (
        <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-700">Filtres</h3>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1">
                <X size={14} /> Effacer tout
              </button>
            )}
          </div>
        </div>
      )}

      {/* Liste */}
      <div>
        <SectorsList
          sectors={filteredSectors}
          selectable={true}
          onAction={(s) => setSelectedSector(s)}
          selectActions={[{
            label: "Supprimer",
            onClick: (selected) => setItemsToDelete(selected.map(i => i.id)),
            className: "bg-red-600 text-white"
          }]}
        />
      </div>

      {/* Menu modal */}
      {selectedSector && (
        <MenuModal
          title={selectedSector.name}
          isOpen={!!selectedSector}
          onClose={() => setSelectedSector(null)}
          icon={<Grid3X3 className="text-primary" size={20} />}
          menu={[
            { label: "Modifier", icon: Grid3X3, onClick: () => navigate(`/${role}/sectors/update?id=${selectedSector.id}`) },
            { label: "Supprimer", icon: MoreVertical, onClick: () => { setSectorToDelete(selectedSector); setSelectedSector(null); } }
          ]}
        />
      )}

      {/* Modals suppression */}
      <DeleteConfirmationModal
        isOpen={!!sectorToDelete}
        onClose={() => setSectorToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer le secteur"
        message={`Supprimer "${sectorToDelete?.name}" ?`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
      />
      <DeleteConfirmationModal
        isOpen={!!itemsToDelete && itemsToDelete.length > 0}
        onClose={() => setItemsToDelete(null)}
        onConfirm={handleDeleteMany}
        title="Suppression multiple"
        message={`Supprimer ${itemsToDelete?.length} secteur(s) ?`}
      />
    </PageLayout>
  );
}